// src/app/components/admin/performance-source-setting.tsx
//
// Platform section of Global Settings: the GLOBAL Performance data source.
// Flips every client's Performance page between synthetic "fixture" data and
// the live "api" endpoint (GET /analytics/series). The value is stored on the
// backend (app_config) and read at runtime — no frontend env var. Admin-only;
// the backend enforces 403 on write for non-admins.
//
// -- Migrated onto the kit, Phase 6 ------------------------------------------
//
// `SettingsPanel` (148), `RadioGroup` (25), `Banner` (62). What did NOT move:
// `fetchPerfSeriesSource`, `updatePerfSeriesSource`, the `analytics:refresh`
// broadcast, and every string.
//
// Two things the swap fixed:
//
//   * It was a radio group drawn as buttons: two mutually exclusive options,
//     one selected, no `role="radiogroup"`, no `aria-checked` and no arrow-key
//     navigation. It is a real radio group now.
//   * `var(--muted, #f1f5f9)` and `var(--destructive-10, rgba(220,38,38,0.08))`
//     were fallbacks for tokens that might not exist — and `--destructive-10`
//     does not, so the error box painted that rgba in both themes.

import { useEffect, useState } from "react";

import {
  fetchPerfSeriesSource,
  updatePerfSeriesSource,
  type SeriesSourceMode,
} from "@/app/services/performance-series/source-setting-api";
import { Banner, RadioGroup, SettingsPanel } from "../kit";

const CHOICES: { id: SeriesSourceMode; label: string; desc: string }[] = [
  {
    id: "fixture",
    label: "Fixture (synthetic)",
    desc: "Renders local synthetic data. Safe default while the live endpoint is being validated.",
  },
  {
    id: "api",
    label: "Live API",
    desc: "Calls the real /analytics/series endpoint so the Performance page shows measured data.",
  },
];

export function PerformanceSourceSetting() {
  const [current, setCurrent] = useState<SeriesSourceMode | null>(null);
  const [saving, setSaving] = useState<SeriesSourceMode | null>(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [savedAt, setSavedAt] = useState(0);

  useEffect(() => {
    let alive = true;
    fetchPerfSeriesSource()
      .then((mode) => alive && setCurrent(mode))
      .catch((e) => alive && setErr(String(e?.message || e)))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, []);

  async function choose(mode: SeriesSourceMode) {
    if (mode === current || saving) return;
    setSaving(mode);
    setErr("");
    try {
      const stored = await updatePerfSeriesSource(mode);
      setCurrent(stored);
      setSavedAt(Date.now());
      // Let the Performance data seam re-read the (now-changed) source setting.
      if (typeof window !== "undefined") {
        window.dispatchEvent(new CustomEvent("analytics:refresh"));
      }
    } catch (e: any) {
      setErr(String(e?.message || e));
    } finally {
      setSaving(null);
    }
  }

  // The description belongs to the option, so it is read with it rather than
  // sitting in a column beside it.
  const activeLabel = CHOICES.find((c) => c.id === current)?.label;

  return (
    <div className="upm-settings-panel-column">
      {/* The explanation is a paragraph in the body, not the panel's
          `description`. Item 148 draws that slot as `k-meta` — nano, uppercase,
          tracked — which is a label treatment, and a two-line sentence set in
          it is unreadable. */}
      <SettingsPanel title="Performance data source">
        <p className="upm-settings-panel-column__lede">
          Controls what powers the Performance page for everyone. Changes take
          effect on the next load, within about a minute, or immediately after a
          manual refresh.
        </p>

        {err ? <Banner tone="error">{err}</Banner> : null}

        <RadioGroup
          label="Performance data source"
          value={current ?? ""}
          onChange={(v) => choose(v as SeriesSourceMode)}
          state={loading || saving ? "disabled" : undefined}
          options={CHOICES.map((c) => ({ value: c.id, label: c.label }))}
        />

        <dl className="upm-settings-panel-column__notes">
          {CHOICES.map((c) => (
            <div key={c.id}>
              <dt className="k-meta">{c.label}</dt>
              <dd>{c.desc}</dd>
            </div>
          ))}
        </dl>

        {savedAt > 0 && !saving && !err && activeLabel ? (
          <Banner tone="ok">
            Saved. All clients will use {activeLabel} for the Performance page.
          </Banner>
        ) : null}
      </SettingsPanel>
    </div>
  );
}

export default PerformanceSourceSetting;
